package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.DateTimeUtils
import co.uk.sacursoftware.ssinventory.util.LocalUserRole
import co.uk.sacursoftware.ssinventory.util.UserRole
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun StockHistoryScreen(
    supplierId: String,
    onBack: () -> Unit,
    onCountClick: (StockCount) -> Unit
) {
    val userRole = LocalUserRole.current
    val scope = rememberCoroutineScope()
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }
    
    var counts by remember { mutableStateOf<List<StockCount>>(emptyList()) }
    var countValuations by remember { mutableStateOf<Map<String, Double>>(emptyMap()) }
    var isLoading by remember { mutableStateOf(true) }

    LaunchedEffect(supplierId) {
        val itemsResult = repository.getItems(supplierId)
        val itemsMap = if (itemsResult.isSuccess) itemsResult.getOrThrow().associateBy { it.id } else emptyMap()

        repository.getStockCounts(supplierId).onSuccess { countList ->
            counts = countList
            if (userRole == UserRole.ADMIN) {
                val vals = mutableMapOf<String, Double>()
                countList.forEach { count ->
                    repository.getStockCountDetails(count.id).onSuccess { details ->
                        var total = 0.0
                        details.forEach { detail ->
                            val item = itemsMap[detail.itemId]
                            if (item != null) {
                                total += calculateItemCost(item, detail.countedRealAmount)
                            }
                        }
                        vals[count.id] = total
                    }
                }
                countValuations = vals
            }
            isLoading = false
        }
    }

    Box(modifier = Modifier.fillMaxSize()) {
        Scaffold(
            topBar = {
                TopAppBar(
                    title = { Text("Count History") },
                    navigationIcon = {
                        IconButton(onClick = onBack) {
                            BackArrowIcon()
                        }
                    }
                )
            }
        ) { padding ->
            Box(modifier = Modifier.padding(padding).fillMaxSize()) {
                if (!isLoading && counts.isEmpty()) {
                    Text("No history found.", modifier = Modifier.align(Alignment.Center))
                } else if (!isLoading) {
                    LazyColumn(modifier = Modifier.fillMaxSize()) {
                        items(counts) { count ->
                            ListItem(
                                headlineContent = { Text(DateTimeUtils.formatCountTimestamp(count.timestamp), fontWeight = FontWeight.Bold) },
                                supportingContent = { 
                                    Column {
                                        count.lastAmended?.let { Text("Last amended: ${DateTimeUtils.formatCountTimestamp(it)}", style = MaterialTheme.typography.bodySmall) }
                                        if (userRole == UserRole.ADMIN && countValuations.containsKey(count.id)) {
                                            val valuation = countValuations[count.id] ?: 0.0
                                            Text("Total Value: £${formatDecimal(valuation)}", style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary)
                                        }
                                    }
                                },
                                modifier = Modifier.clickable { onCountClick(count) }
                            )
                            HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.1f))
                        }
                    }
                }
            }
        }

        if (isLoading) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(MaterialTheme.colorScheme.background)
                    .clickable(enabled = false) { },
                contentAlignment = Alignment.Center
            ) {
                BrandLoadingSpinner(Modifier.fillMaxSize())
            }
        }
    }
}
